import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider.tsx';
import { Merkehode } from '../skall/Merke.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { useSidetittel } from '../skall/tittel.ts';

/**
 * Innlogging: e-post med engangskode, og Google når leietakerens prosjekt har det.
 * Ingen «husk meg» (K8). Feilmeldingene sier hva som skjedde og hva som fikser det;
 * adressen beholdes.
 */
export function Innlogging({ googleLogin }: { googleLogin: boolean }) {
  const auth = useAuth();
  useSidetittel('Logg inn');
  const [epost, setEpost] = useState('');
  const [kode, setKode] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const [nySendt, setNySendt] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);
  const t = auth.tilstand;

  const kjør = async (fn: () => Promise<void>) => {
    setFeil(null);
    setNySendt(null);
    setOpptatt(true);
    try {
      await fn();
    } catch (e) {
      setFeil(e instanceof Error ? e.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setOpptatt(false);
    }
  };

  const sendKode = (e: FormEvent) => {
    e.preventDefault();
    void kjør(() => auth.sendKode(epost.trim()));
  };
  const bekreft = (e: FormEvent) => {
    e.preventDefault();
    void kjør(() => auth.bekreftKode(kode));
  };
  // Til samme adresse (H2.5-funn 2). Feiler leverandøren, står leverandørens setning og kodesteget
  // blir stående; den gamle koden kan fortsatt virke.
  const sendNy = (adresse: string) =>
    void kjør(async () => {
      await auth.sendKode(adresse);
      setKode('');
      setNySendt(adresse);
    });

  return (
    <main className="panel-enkelt" id="innhold">
      <section className="panel-kort" aria-labelledby="innlogging-h">
        <Merkehode storrelse="xl">
          <div className="panel-merke-under">Adminpanel</div>
        </Merkehode>
        <h1 id="innlogging-h" className="panel-h1">
          Logg inn
        </h1>
        {t.navn === 'utlogget' && t.melding ? (
          <p role="status" className="panel-melding panel-melding-info">
            {t.melding}
          </p>
        ) : null}

        {t.navn === 'kode-sendt' ? (
          <form onSubmit={bekreft} className="panel-skjema">
            <p className="panel-tekst">
              Vi sendte en engangskode til <strong>{t.epost}</strong>. Den varer i noen minutter.
            </p>
            <label className="panel-felt">
              <span>Engangskode</span>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" required value={kode} onChange={(e) => setKode(e.target.value)} disabled={opptatt} autoFocus />
            </label>
            {feil ? (
              <p role="alert" className="panel-melding panel-melding-feil">
                {feil}
              </p>
            ) : null}
            {nySendt ? (
              <p role="status" className="panel-melding panel-melding-info">
                {`Ny kode sendt til ${nySendt}.`}
              </p>
            ) : null}
            <div className="panel-handlinger">
              <button type="submit" className="panel-knapp" disabled={opptatt}>
                Bekreft
              </button>
              <button type="button" className="panel-knapp-sekundaer" onClick={() => sendNy(t.epost)} disabled={opptatt}>
                Send ny kode
              </button>
              <button type="button" className="panel-knapp-sekundaer" onClick={() => auth.tilbakeTilEpost()} disabled={opptatt}>
                Bruk en annen adresse
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={sendKode} className="panel-skjema">
            {/* DD-79: Google først når prosjektet har det — ett klikk for den som har Google, og
                feltet rett under for den som ikke har. Rekkefølgen i DOM er rekkefølgen
                tabulatoren og skjermleseren går i. Uten Google er siden som før. */}
            {googleLogin ? (
              <>
                <button type="button" className="panel-knapp panel-knapp-bred panel-knapp-ikon" onClick={() => void kjør(() => auth.loggInnMedGoogle())} disabled={opptatt}>
                  <Ikon navn="logo-google" />
                  Logg inn med Google
                </button>
                <p className="panel-skille" data-testid="innlogging-eller">
                  <span>eller</span>
                </p>
              </>
            ) : null}
            <label className="panel-felt">
              <span>Jobbadresse</span>
              <input type="email" autoComplete="email" required value={epost} onChange={(e) => setEpost(e.target.value)} disabled={opptatt} />
            </label>
            {feil ? (
              <p role="alert" className="panel-melding panel-melding-feil">
                {feil}
              </p>
            ) : null}
            <div className="panel-handlinger">
              <button type="submit" className={googleLogin ? 'panel-knapp-sekundaer' : 'panel-knapp'} disabled={opptatt}>
                Send engangskode
              </button>
            </div>
            <p className="panel-tekst panel-tekst-liten">Krever virksomheten din tofaktor, blir du bedt om en kode fra appen etter innloggingen.</p>
          </form>
        )}
        {/* Art. 13: informasjonen der opplysningene samles inn (K36). En vanlig lenke, ikke en
            rute: /personvern/ er en statisk side i bygget, uten appen. */}
        <p className="panel-tekst panel-tekst-liten">
          <a href="/personvern/">Personvern for panelbrukere</a>
        </p>
      </section>
    </main>
  );
}
